import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  Search, Filter, X, ShoppingCart, Loader2, ChevronDown, ChevronUp, Plus, Trash2, FileCheck, Link2,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import {
  PENDING_PC_LABEL, isPendingPc, dealerTag, needsCustomerFallback, fallbackCustomerText,
} from './salesOrderListDisplay';
import { canTagOrder, isPickMode, savePick } from '../admin/followUpPick';

interface SalesOrder {
  id: string;
  number: string;
  status: string;
  orderDate: string;
  deliveryDate?: string | null;
  total: number;
  isExport: boolean;
  gateStatus?: string | null;
  currency: string;
  fulfilledQtyPct: number;
  invoicedQtyPct: number;
  contact?: { id: string; firstName: string; lastName: string } | null;
  account?: { id: string; name: string } | null;
  _count?: { items: number };
  // Round E2 (spec R8/R9) — additive API fields.
  paymentConfirmationStatus?: 'pending' | 'confirmed' | 'rejected' | null;
  customerName?: string | null;
  dealerName?: string | null;
  buyerType?: string | null;
}

// Full 12-state spec for Sun Stellar SO lifecycle
const STATUSES = [
  'draft',
  'confirmed',
  'payment_pending',
  'in_manufacturing',
  'qc_in_progress',
  'processing',
  'dispatched',
  'in_transit',
  'partial_shipped',
  'shipped',
  'delivered',
  'mismatch_pending',
  'fulfilled',
  'cancelled',
  'deleted',
];

/** The fixed list plus any status the server has sent that is not in it. */
export function statusFilterOptions(seen: string[] = []): string[] {
  return [...STATUSES, ...seen.filter((s) => s && !STATUSES.includes(s))];
}

/** The server's reason for a refused delete (403 / 409), else a plain fallback. */
export function deleteErrorMessage(err: any): string {
  return err?.response?.data?.error || 'Failed to delete';
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  confirmed: 'bg-blue-50 text-blue-700',
  payment_pending: 'bg-amber-50 text-amber-700',
  in_manufacturing: 'bg-purple-50 text-purple-700',
  qc_in_progress: 'bg-cyan-50 text-cyan-700',
  processing: 'bg-yellow-50 text-yellow-700',
  dispatched: 'bg-sky-50 text-sky-700',
  in_transit: 'bg-teal-50 text-teal-700',
  partial_shipped: 'bg-orange-50 text-orange-700',
  shipped: 'bg-indigo-50 text-indigo-700',
  delivered: 'bg-green-50 text-green-700',
  mismatch_pending: 'bg-rose-50 text-rose-700',
  fulfilled: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-50 text-red-700',
  deleted: 'bg-red-50 text-red-700',
};

function formatStatus(s: string) {
  return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function SalesOrdersPage() {
  const navigate = useNavigate();
  // 2026-10-09 — opened from Mark follow-up done → Tag sale: this lead's
  // orders only (server leadId filter) + a "Tag sale" button per row.
  const [searchParams] = useSearchParams();
  const pick = isPickMode(searchParams);
  const pickLeadId = pick?.leadId ?? null;
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  // ?status=confirmed from the Inventory Dashboard card; an unknown value = no filter.
  const urlStatus = STATUSES.includes(searchParams.get('status') ?? '') ? (searchParams.get('status') as string) : '';
  const [filterStatus, setFilterStatus] = useState(urlStatus);
  const [showFilters, setShowFilters] = useState(!!urlStatus);

  const [sortField, setSortField] = useState<string>('orderDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const [seenStatuses, setSeenStatuses] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (pickLeadId) params.leadId = pickLeadId;
      params.page = String(page);
      params.limit = String(limit);
      params.sortBy = sortField;
      params.sortDir = sortDir;
      const res = await api.get('/sales-orders', { params });
      const responseData = res.data.data;
      if (Array.isArray(responseData)) {
        setOrders(responseData);
        // The API answers with the rows and a separate pagination block.
        setTotal(res.data?.pagination?.total ?? responseData.length);
        setSeenStatuses((prev) => Array.from(new Set([...prev, ...responseData.map((o: SalesOrder) => o.status)])));
      } else {
        setOrders(responseData?.salesOrders ?? responseData?.orders ?? []);
        setTotal(responseData?.total ?? 0);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setOrders([]);
      } else {
        setError(err.response?.data?.error || 'Failed to load sales orders');
      }
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, page, pickLeadId, sortField, sortDir]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, sortField, sortDir]);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Orders</h1>
          <p className="text-sm text-gray-500">Manage sales orders, fulfillment, and invoicing</p>
        </div>
        <button
          onClick={() => navigate('/sales-orders/new')}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Create Sales Order
        </button>
      </div>

      {pick && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm text-gray-800">
          <span>This lead's sales only. Tag the sale for this follow-up.</span>
          <button type="button" onClick={() => navigate(pick.ret)} className="text-sm text-gray-600 underline">Back</button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search sales orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Statuses</option>
            {statusFilterOptions(seenStatuses).map(s => (
              <option key={s} value={s}>{formatStatus(s)}</option>
            ))}
          </select>
          {filterStatus && (
            <button
              onClick={() => setFilterStatus('')}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading sales orders...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchOrders} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <ShoppingCart size={40} className="mb-2" />
            <p className="text-sm">No sales orders found</p>
            <p className="text-xs">Sales orders will appear here once created</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('number')}>
                    <span className="flex items-center gap-1">Number <SortIcon field="number" /></span>
                  </th>
                  <th className="px-4 py-3">Contact / Account</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('orderDate')}>
                    <span className="flex items-center gap-1">Order Date <SortIcon field="orderDate" /></span>
                  </th>
                  <th className="px-4 py-3">Fulfillment</th>
                  <th className="cursor-pointer px-4 py-3 text-right" onClick={() => handleSort('total')}>
                    <span className="flex items-center justify-end gap-1">Total <SortIcon field="total" /></span>
                  </th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map(so => (
                  <tr key={so.id} onClick={() => navigate(`/sales-orders/${so.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3 font-medium text-[var(--ink-0)]">
                      {so.number}
                      {so.isExport && <span className="ml-1.5 rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">EXPORT</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {so.contact ? `${joinPersonName(so.contact.firstName, so.contact.lastName)}` : ''}
                      {so.contact && so.account ? ' / ' : ''}
                      {so.account?.name || ''}
                      {needsCustomerFallback(so) && fallbackCustomerText(so)}
                      {dealerTag(so) && (
                        <span className="ml-1.5 text-xs text-gray-400">{dealerTag(so)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[so.status] || STATUS_COLORS.draft}`}>
                        {formatStatus(so.status)}
                      </span>
                      {isPendingPc(so.paymentConfirmationStatus) && (
                        <span className="ml-1.5 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                          {PENDING_PC_LABEL}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(so.orderDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-gray-200">
                          <div
                            className="h-1.5 rounded-full bg-green-500 transition-all"
                            style={{ width: `${Math.min(so.fulfilledQtyPct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{Math.round(so.fulfilledQtyPct)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{formatINR(so.total)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {pick && canTagOrder(so) && (
                          <button type="button" data-testid={`tag-sale-${so.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              savePick(pick.leadId, { kind: 'sale', id: so.id, number: so.number ?? null, total: so.total != null ? Number(so.total) : null });
                              navigate(pick.ret);
                            }}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
                            <Link2 size={13} /> Tag sale
                          </button>
                        )}
                        {so.status === 'draft' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sales-orders/${so.id}/edit`);
                              }}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <FileCheck size={15} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(`Delete sales order ${so.number}?`)) {
                                  api.delete(`/sales-orders/${so.id}`).then(() => fetchOrders()).catch((err) => alert(deleteErrorMessage(err)));
                                }
                              }}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
              <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
